import React, { useMemo, useState } from "react";
import Button from "../inputs/Button";
import Badge from "../shared/Badge";
import CadreDetailsModal from "../shared/CadreDetailsModal";
import ReusableTable from "../shared/ReusableTable";
import { handleRovingTabKeyDown } from "../../utils/tabKeyboard";
import {
  APPLICATION_STATUS_OPTIONS,
  filterApplications,
} from "../../utils/applicationStatus";

const AdminDashboardView = ({
  user,
  data,
  onNavigate,
  onRefresh,
  onAction,
  cadrePagination,
  onCadrePageChange,
  cadreLoading = false,
}) => {
  const {
    stats = {},
    panels = [],
    applications = [],
    adminUsers = [],
    cadreMembers = [],
  } = data || {};

  const [activeTab, setActiveTab] = useState("applications-process");
  const [showApplicationModal, setShowApplicationModal] = useState(false);
  const [selectedApplication, setSelectedApplication] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [showCadreModal, setShowCadreModal] = useState(false);
  const [selectedCadre, setSelectedCadre] = useState(null);
  const [appSearch, setAppSearch] = useState("");
  const [appStatus, setAppStatus] = useState("ALL");
  const [appFromDate, setAppFromDate] = useState("");
  const [appToDate, setAppToDate] = useState("");

  const handleViewApplicationDetails = (application) => {
    setSelectedApplication(application);
    setShowApplicationModal(true);
  };

  const handleApproveApplication = async (applicationId) => {
    setActionLoading(true);
    try {
      if (onAction) {
        await onAction({ type: "approveApplication", id: applicationId });
      }
      setShowApplicationModal(false);
      setSelectedApplication(null);
    } catch (error) {
      console.error("Error approving application:", error);
    } finally {
      setActionLoading(false);
    }
  };

  const handleRejectApplication = async (applicationId, reason) => {
    setActionLoading(true);
    try {
      if (onAction) {
        await onAction({
          type: "rejectApplication",
          id: applicationId,
          reason,
        });
      }
      setShowApplicationModal(false);
      setSelectedApplication(null);
    } catch (error) {
      console.error("Error rejecting application:", error);
    } finally {
      setActionLoading(false);
    }
  };

  const handleCloseApplicationModal = () => {
    setShowApplicationModal(false);
    setSelectedApplication(null);
  };

  const handleDeleteApplication = async (
    applicationId,
    { confirmed = false } = {}
  ) => {
    if (
      !confirmed &&
      !window.confirm(
        "Permanently delete this application? This cannot be undone."
      )
    ) {
      return;
    }

    setActionLoading(true);
    try {
      if (onAction) {
        await onAction({ type: "deleteApplication", id: applicationId });
      }
      setShowApplicationModal(false);
      setSelectedApplication(null);
    } catch (error) {
      console.error("Error deleting application:", error);
    } finally {
      setActionLoading(false);
    }
  };

  const handleViewMemberDetails = (member) => {
    setSelectedCadre(member);
    setShowCadreModal(true);
  };

  const handleCloseCadreModal = () => {
    setShowCadreModal(false);
    setSelectedCadre(null);
  };

  const getStatusVariant = (status) => {
    switch (status) {
      case "APPROVED":
      case "Active":
      case "ACTIVE":
      case "COMPLETED":
        return "success";
      case "IN_PROGRESS":
      case "IN_REVIEW":
      case "PENDING":
      case "SUBMITTED":
        return "warning";
      case "REJECTED":
      case "Closed":
      case "CLOSED":
      case "INACTIVE":
        return "danger";
      case "PENDING_CERTIFICATION":
      case "PENDING_APPROVAL":
        return "primary";
      case "PENDING_PUBLICATION":
      case "DECISION_REACHED":
        return "purple";
      case "PUBLISHED":
        return "info";
      default:
        return "default";
    }
  };

  const getTabData = () => {
    switch (activeTab) {
      case "applications-process":
        return filterApplications(applications, {
          search: appSearch,
          status: appStatus,
          fromDate: appFromDate,
          toDate: appToDate,
        });
      case "cadre-members":
        return cadreMembers;
      case "applications-certification":
        return filterApplications(applications, {
          search: appSearch,
          status: "PENDING_CERTIFICATION",
          fromDate: appFromDate,
          toDate: appToDate,
        });
      case "panels-process":
        return panels.filter(
          (panel) => panel.panelStatus === "Active" || panel.status === "Active"
        );
      case "panels-approval":
        return panels.filter(
          (panel) => panel.panelStatus === "Closed" || panel.status === "Closed"
        );
      case "admin-users":
        return adminUsers;
      default:
        return [];
    }
  };
  const getConflictStatusColor = (status) => {
    switch (status) {
      case "YES":
        return "bg-red-100 text-red-800";
      case "NO":
        return "bg-green-100 text-green-800";
      case "NOT_ANSWERED":
        return "bg-yellow-100 text-yellow-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };
  const tabData = getTabData();
  const hasAppFilters = Boolean(
    appSearch ||
      (appStatus && appStatus !== "ALL") ||
      appFromDate ||
      appToDate
  );
  const applicationCounts = useMemo(() => {
    return {
      total: applications.length,
      pending: filterApplications(applications, { status: "IN_PROCESS" })
        .length,
      approved: applications.filter((app) => app.status === "APPROVED").length,
      rejected: applications.filter((app) => app.status === "REJECTED").length,
    };
  }, [applications]);

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">
          Administrator Dashboard
        </h1>
        <p className="text-gray-600 mt-2">Welcome back, {user?.name}</p>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-8">
        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">All Applications</p>
              <p className="text-2xl font-bold text-custom-blue">
                {stats.totalApplications ?? applicationCounts.total}
              </p>
              <p className="mt-1 text-xs text-gray-600">
                {stats.pendingApplications ?? applicationCounts.pending} pending
                {" · "}
                {stats.approvedApplications ?? applicationCounts.approved} approved
                {" · "}
                {stats.rejectedApplications ?? applicationCounts.rejected} rejected
              </p>
            </div>
            <div className="bg-yellow-100 p-2 rounded-lg">
              <svg
                className="w-6 h-6 text-yellow-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                />
              </svg>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Active Panels</p>
              <p className="text-2xl font-bold text-blue-600">
                {stats.activePanels || 0}
              </p>
            </div>
            <div className="bg-blue-100 p-2 rounded-lg">
              <svg
                className="w-6 h-6 text-blue-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
                />
              </svg>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Total Panels</p>
              <p className="text-2xl font-bold text-purple-600">
                {stats.totalPanels || 0}
              </p>
            </div>
            <div className="bg-purple-100 p-2 rounded-lg">
              <svg
                className="w-6 h-6 text-purple-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Total Cadre</p>
              <p className="text-2xl font-bold text-green-600">
                {stats.totalCadreMembers || 0}
              </p>
            </div>
            <div className="bg-green-100 p-2 rounded-lg">
              <svg
                className="w-6 h-6 text-green-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z"
                />
              </svg>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Admin Users</p>
              <p className="text-2xl font-bold text-gray-700">
                {adminUsers.length}
              </p>
            </div>
            <div className="bg-gray-100 p-2 rounded-lg">
              <svg
                className="w-6 h-6 text-gray-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
                />
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                />
              </svg>
            </div>
          </div>
        </div>
      </div>

      {/* Main Action Buttons */}
      <div className="mb-6 flex flex-wrap gap-4">
        <Button
          onClick={() => onNavigate("panel-creation")}
          variant="primary"
          className="bg-custom-blue hover:bg-custom-light-blue shadow-lg"
        >
          <svg
            className="w-5 h-5 mr-2"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 4v16m8-8H4"
            />
          </svg>
          Create New Panel
        </Button>

        <Button
          onClick={() => onAction && onAction({ type: "exportFullReport" })}
          variant="primary"
          className="bg-emerald-800 hover:bg-emerald-900 shadow-lg"
        >
          <svg
            className="w-5 h-5 mr-2"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
            />
          </svg>
          Export Full Report
        </Button>

        <Button
          onClick={() => onAction && onAction({ type: "exportPanels" })}
          variant="primary"
          className="bg-gray-600 hover:bg-gray-700 shadow-lg"
        >
          <svg
            className="w-5 h-5 mr-2"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
            />
          </svg>
          Export Panels
        </Button>
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-lg shadow">
        <div className="border-b border-gray-200">
          <div
            className="flex -mb-px overflow-x-auto"
            role="tablist"
            aria-label="Admin dashboard sections"
          >
            {[
              { id: "applications-process", label: "All Applications" },
              {
                id: "applications-certification",
                label: "Pending Certification",
              },
              { id: "cadre-members", label: "Cadre Members" },
              { id: "panels-process", label: "Active Panels" },
              { id: "panels-approval", label: "Closed Panels" },
              { id: "admin-users", label: "Admin Users" },
            ].map((tab, index, adminTabs) => (
              <button
                key={tab.id}
                type="button"
                role="tab"
                id={`admin-tab-${tab.id}`}
                aria-selected={activeTab === tab.id}
                aria-controls={`admin-panel-${tab.id}`}
                tabIndex={activeTab === tab.id ? 0 : -1}
                onClick={() => setActiveTab(tab.id)}
                onKeyDown={(e) =>
                  handleRovingTabKeyDown(
                    e,
                    index,
                    adminTabs,
                    setActiveTab,
                    (t) => `admin-tab-${t.id}`
                  )
                }
                className={`py-4 px-6 border-b-2 font-medium text-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-600 ${
                  activeTab === tab.id
                    ? "border-custom-blue text-custom-blue"
                    : "border-transparent text-gray-700 hover:text-gray-900 hover:border-gray-300"
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        {/* Tab Content */}
        <div id={`admin-panel-${activeTab}`} role="tabpanel" aria-labelledby={`admin-tab-${activeTab}`} className="p-6">
          {/* Applications Table */}
          {(activeTab === "applications-process" ||
            activeTab === "applications-certification") && (
            <>
              <form
                className="mb-6 grid gap-4 md:grid-cols-2 xl:grid-cols-5"
                onSubmit={(e) => e.preventDefault()}
              >
                <div className="xl:col-span-2">
                  <label
                    htmlFor="application-search"
                    className="mb-1 block text-sm font-medium text-gray-900"
                  >
                    Search
                  </label>
                  <input
                    id="application-search"
                    type="search"
                    value={appSearch}
                    onChange={(e) => setAppSearch(e.target.value)}
                    placeholder="Name, email, or expertise"
                    className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-900 focus:border-custom-blue focus:outline-none focus:ring-2 focus:ring-custom-blue"
                  />
                </div>
                <div>
                  <label
                    htmlFor="application-status"
                    className="mb-1 block text-sm font-medium text-gray-900"
                  >
                    Status
                  </label>
                  <select
                    id="application-status"
                    value={
                      activeTab === "applications-certification"
                        ? "PENDING_CERTIFICATION"
                        : appStatus
                    }
                    onChange={(e) => setAppStatus(e.target.value)}
                    disabled={activeTab === "applications-certification"}
                    className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-custom-blue focus:outline-none focus:ring-2 focus:ring-custom-blue disabled:bg-gray-100"
                  >
                    {APPLICATION_STATUS_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label
                    htmlFor="application-from"
                    className="mb-1 block text-sm font-medium text-gray-900"
                  >
                    From
                  </label>
                  <input
                    id="application-from"
                    type="date"
                    value={appFromDate}
                    onChange={(e) => setAppFromDate(e.target.value)}
                    className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-900 focus:border-custom-blue focus:outline-none focus:ring-2 focus:ring-custom-blue"
                  />
                </div>
                <div>
                  <label
                    htmlFor="application-to"
                    className="mb-1 block text-sm font-medium text-gray-900"
                  >
                    To
                  </label>
                  <input
                    id="application-to"
                    type="date"
                    value={appToDate}
                    onChange={(e) => setAppToDate(e.target.value)}
                    className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-900 focus:border-custom-blue focus:outline-none focus:ring-2 focus:ring-custom-blue"
                  />
                </div>
              </form>
              <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
                <p className="text-sm text-gray-700">
                  Showing {tabData.length} of {applications.length} applications
                </p>
                {hasAppFilters && activeTab === "applications-process" && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setAppSearch("");
                      setAppStatus("ALL");
                      setAppFromDate("");
                      setAppToDate("");
                    }}
                  >
                    Clear filters
                  </Button>
                )}
              </div>
            <ReusableTable
              columns={[
                {
                  key: "applicantName",
                  label: "Applicant",
                  render: (app) => (
                    <div>
                      <div className="text-sm font-medium text-gray-900">
                        {app.applicantName}
                      </div>
                      <div className="text-sm text-gray-500">{app.email}</div>
                    </div>
                  ),
                },
                {
                  key: "expertise",
                  label: "Expertise",
                  render: (app) => (
                    <div className="text-sm text-gray-900">
                      {Array.isArray(app.expertise)
                        ? app.expertise.join(", ")
                        : app.expertise || "N/A"}
                    </div>
                  ),
                },
                {
                  key: "yearsExperience",
                  label: "Experience",
                  render: (app) => `${app.yearsExperience || 0} years`,
                },
                {
                  key: "status",
                  label: "Status",
                  render: (app) => (
                    <Badge variant={getStatusVariant(app.status)} size="sm">
                      {app.status.replace(/_/g, " ")}
                    </Badge>
                  ),
                },
                {
                  key: "submittedDate",
                  label: "Submitted",
                  render: (app) => {
                    const date = app.submittedDate || app.createdAt;
                    return date ? new Date(date).toLocaleDateString() : "N/A";
                  },
                  cellClassName: "text-gray-500",
                },
                {
                  key: "actions",
                  label: "Actions",
                  render: (app) => (
                    <div className="flex flex-wrap gap-2">
                      <Button
                        onClick={() => handleViewApplicationDetails(app)}
                        variant="primary"
                        size="sm"
                      >
                        View Details
                      </Button>
                      <Button
                        onClick={() => handleDeleteApplication(app.id)}
                        variant="danger"
                        size="sm"
                      >
                        Delete
                      </Button>
                    </div>
                  ),
                },
              ]}
              data={tabData}
              emptyStateMessage={
                hasAppFilters
                  ? "No applications match these filters"
                  : "No applications found"
              }
            />
            </>
          )}

          {/* Cadre Members Table */}
          {activeTab === "cadre-members" && (
            <ReusableTable
              columns={[
                {
                  key: "name",
                  label: "Name",
                  render: (member) => (
                    <div>
                      <div className="text-sm font-medium text-gray-900">
                        {member.user?.name || member.name || "Unknown Member"}
                      </div>
                      <div className="text-sm text-gray-500">
                        {member.user?.email || member.email || "No email"}
                      </div>
                    </div>
                  ),
                },
                {
                  key: "conflictOfInterest",
                  label: "Conflict of Interest",
                  render: (member) => (
                    <span
                      className={`px-2 py-1 rounded-full text-xs font-medium ${getConflictStatusColor(
                        member.conflictOfInterests?.evaluatedStatus ||
                          "NOT_ANSWERED"
                      )}`}
                    >
                      {member.conflictOfInterests?.evaluatedStatus ||
                        "NOT_ANSWERED"}
                    </span>
                  ),
                },
                {
                  key: "experience",
                  label: "Experience",
                  render: (member) => (
                    <span className="text-sm text-gray-900">
                      {member.yearsExperience ||
                        member.cadreProfile?.yearsExperience ||
                        0}{" "}
                      years
                    </span>
                  ),
                },
                {
                  key: "rating",
                  label: "Rating",
                  render: (member) => (
                    <span className="text-sm text-gray-900">
                      {member.cadreProfile?.rating
                        ? `${member.cadreProfile.rating}/5`
                        : "Not rated"}
                    </span>
                  ),
                },
                {
                  key: "actions",
                  label: "Actions",
                  render: (member) => (
                    <Button
                      variant="primary"
                      size="sm"
                      onClick={() => handleViewMemberDetails(member)}
                    >
                      View Details
                    </Button>
                  ),
                },
              ]}
              data={tabData}
              emptyStateMessage="No cadre members found"
              pagination={cadrePagination}
              onPageChange={onCadrePageChange}
              paginationLoading={cadreLoading}
            />
          )}

          {/* Panels List */}
          {(activeTab === "panels-process" ||
            activeTab === "panels-approval") && (
            <div className="space-y-4">
              {tabData.map((panel) => (
                <div
                  key={panel.id}
                  className="bg-white border border-gray-200 rounded-lg p-5 hover:shadow-lg transition-shadow"
                >
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <div className="flex items-center mb-2">
                        <h3 className="text-lg font-semibold text-gray-900 mr-3">
                          {panel.panelName}
                        </h3>
                        <Badge
                          variant={getStatusVariant(
                            panel.panelStatus || panel.status
                          )}
                          size="sm"
                        >
                          {(
                            panel.panelStatus ||
                            panel.status ||
                            "Active"
                          ).replace(/_/g, " ")}
                        </Badge>
                      </div>
                      <p className="text-sm text-gray-600 mb-2">
                        {panel.panelType}
                      </p>
                      <div className="flex items-center space-x-6 text-sm text-gray-500">
                        <span>
                          Created:{" "}
                          {new Date(panel.createdAt).toLocaleDateString()}
                        </span>
                        {panel.deadline && (
                          <span>
                            Deadline:{" "}
                            {new Date(panel.deadline).toLocaleDateString()}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="ml-4 flex flex-col space-y-2">
                      <Button
                        onClick={() => onNavigate(`panel-details/${panel.id}`)}
                        variant="secondary"
                        size="sm"
                      >
                        View Details
                      </Button>
                      {(panel.panelStatus === "Active" ||
                        panel.status === "Active") && (
                        <Button
                          onClick={() =>
                            onAction &&
                            onAction({ type: "publishPanel", id: panel.id })
                          }
                          variant="primary"
                          size="sm"
                          className="bg-purple-600 hover:bg-purple-700"
                        >
                          Publish Panel
                        </Button>
                      )}
                      {(panel.panelStatus === "Active" ||
                        panel.status === "Active") && (
                        <Button
                          onClick={() =>
                            onAction &&
                            onAction({ type: "closePanel", id: panel.id })
                          }
                          variant="primary"
                          size="sm"
                          className="bg-red-600 hover:bg-red-700"
                        >
                          Close Panel
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
              {tabData.length === 0 && (
                <div className="text-center py-8 text-gray-500">
                  No panels{" "}
                  {activeTab === "panels-approval"
                    ? "that are closed"
                    : "that are active"}
                </div>
              )}
            </div>
          )}

          {/* Admin Users Table */}
          {activeTab === "admin-users" && (
            <ReusableTable
              columns={[
                {
                  key: "name",
                  label: "User",
                  render: (adminUser) => (
                    <div>
                      <div className="text-sm font-medium text-gray-900">
                        {adminUser.name}
                      </div>
                      <div className="text-sm text-gray-500">
                        {adminUser.email}
                      </div>
                    </div>
                  ),
                },
                {
                  key: "role",
                  label: "Role",
                  render: (adminUser) => (
                    <span className="text-sm text-gray-900">
                      {adminUser.role.replace("_", " ")}
                    </span>
                  ),
                },
                {
                  key: "isActive",
                  label: "Status",
                  render: (adminUser) => (
                    <Badge
                      variant={getStatusVariant(
                        adminUser.isActive ? "ACTIVE" : "INACTIVE"
                      )}
                      size="sm"
                    >
                      {adminUser.isActive ? "ACTIVE" : "INACTIVE"}
                    </Badge>
                  ),
                },
                {
                  key: "lastLogin",
                  label: "Last Login",
                  render: (adminUser) =>
                    adminUser.lastLogin
                      ? new Date(adminUser.lastLogin).toLocaleDateString()
                      : "Never",
                  cellClassName: "text-gray-500",
                },
              ]}
              data={tabData}
              emptyStateMessage="No admin users found"
            />
          )}
        </div>
      </div>

      {/* Application Details Modal (using unified CadreDetailsModal) */}
      <CadreDetailsModal
        isOpen={showApplicationModal}
        onClose={handleCloseApplicationModal}
        type="application"
        data={selectedApplication}
        onApprove={handleApproveApplication}
        onReject={handleRejectApplication}
        onDelete={(id) => handleDeleteApplication(id, { confirmed: true })}
        loading={actionLoading}
      />

      {/* Cadre Member Details Modal */}
      <CadreDetailsModal
        isOpen={showCadreModal}
        onClose={handleCloseCadreModal}
        type="cadre"
        data={selectedCadre}
      />
    </div>
  );
};

export default AdminDashboardView;
